import { createBrowserRouter, redirect, Navigate, Outlet } from 'react-router-dom';
import { ensureSession, isPlatformAdmin, withFeature } from '@/entities/session';
import { Feature } from '@/shared/lib/features';
import { PersonaShell } from '@/widgets/app-shell/PersonaShell';
import { RouteError } from '@/app/RouteError';
import { LoginPage } from '@/pages/login/LoginPage';
import { KidLoginPage } from '@/pages/kid-login/KidLoginPage';
import { HomePage } from '@/pages/home/HomePage';
import { ChoreDetailPage } from '@/pages/chore-detail/ChoreDetailPage';

// Chore detail is on the kid critical path (eager), but still hidden behind a calm "not enabled" state
// when the household has the Chores module turned off — the deep-link backstop behind the hidden nav.
const ChoreDetail = withFeature(Feature.Chores, ChoreDetailPage);

/**
 * RR7 data-mode router (ssr:false). The root loader resolves `me` (cold loads attempt a transparent
 * refresh first). Route guards send unauthenticated users to `/login` and pick the persona shell.
 *
 * Code-splitting: the kid critical path (home/today + chore detail) is eager so first interaction is
 * fast; the heavier secondary screens (rewards, wallet, savings, profile, calendar, approvals) and
 * the Phase-6 admin surface are LAZY route chunks so they never inflate the kid initial bundle.
 */

/** The current in-app destination (path + query), so login can resume a deep link after auth. */
function loginRedirect(request: Request): Response {
  const url = new URL(request.url);
  const from = url.pathname + url.search;
  // '/' is the default landing anyway — no point round-tripping it through `?from`.
  return from === '/' ? redirect('/login') : redirect(`/login?from=${encodeURIComponent(from)}`);
}

/** Guarded loader for the authenticated area: resolve the session or bounce to login. */
async function protectedLoader({ request }: { request: Request }): Promise<Response | null> {
  const user = await ensureSession();
  if (!user) {
    throw loginRedirect(request);
  }
  // A super-admin (ADR-0072) has no household — the family / household-admin shells aren't for them.
  if (isPlatformAdmin(user)) {
    throw redirect('/platform');
  }
  return null;
}

/** Guard for the standalone platform console: a super-admin OR an env-allowlisted owner. */
async function platformLoader({ request }: { request: Request }): Promise<Response | null> {
  const user = await ensureSession();
  if (!user) {
    throw loginRedirect(request);
  }
  if (!isPlatformAdmin(user) && user.isPlatformOwner !== true) {
    throw redirect('/');
  }
  return null;
}

/** The login route redirects to the right home if already authenticated. */
async function loginLoader(): Promise<Response | null> {
  const user = await ensureSession();
  if (user) {
    throw redirect(isPlatformAdmin(user) ? '/platform' : '/');
  }
  return null;
}

export const router = createBrowserRouter([
  {
    // Pathless layout grouping the PUBLIC (pre-auth) routes + the `*` catch-all so they share an
    // `errorElement`: a lazy-chunk rejection on /accept-invite, /reset-password, etc. (or a render
    // throw) shows the kind ErrorState instead of white-screening, matching the authenticated trees.
    // It renders only the matched child (no shell of its own).
    element: <Outlet />,
    errorElement: <RouteError />,
    children: [
      {
        path: '/login',
        loader: loginLoader,
        element: <LoginPage />,
      },
      {
        // Kid-friendly entry on a shared device (avatar picker + PIN). Same redirect-if-authed guard.
        path: '/kids',
        loader: loginLoader,
        element: <KidLoginPage />,
      },
      {
        // Public invite-redemption: a relative opens the link from their invite, sets a password, and the
        // single-use token activates their account. No session guard — the token IS the credential, and it
        // belongs to the invitee (not whoever might be authed on this device). LAZY so it stays out of the
        // kid critical-path bundle.
        path: '/accept-invite/:token',
        lazy: async () => {
          const { AcceptInvitePage } = await import('@/pages/accept-invite/AcceptInvitePage');
          return { Component: AcceptInvitePage };
        },
      },
      {
        // Public: request a password-reset link by email (ADR-0071). No session guard. LAZY.
        path: '/forgot-password',
        lazy: async () => {
          const { ForgotPasswordPage } = await import('@/pages/forgot-password/ForgotPasswordPage');
          return { Component: ForgotPasswordPage };
        },
      },
      {
        // Public: redeem a reset token + set a new password (ADR-0071). The token IS the credential. LAZY.
        path: '/reset-password/:token',
        lazy: async () => {
          const { ResetPasswordPage } = await import('@/pages/reset-password/ResetPasswordPage');
          return { Component: ResetPasswordPage };
        },
      },
      {
        // Public: redeem an email-verification token (ADR-0071 soft-gate). Openable authed or not. LAZY.
        path: '/verify-email/:token',
        lazy: async () => {
          const { VerifyEmailPage } = await import('@/pages/verify-email/VerifyEmailPage');
          return { Component: VerifyEmailPage };
        },
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
  {
    path: '/',
    loader: protectedLoader,
    element: <PersonaShell />,
    // A render/loader throw anywhere in the authenticated tree renders the kind ErrorState instead of
    // white-screening the app (RR7 error boundary).
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomePage /> },
      // Legacy `/today` link/bookmark → home (the kid home IS the day screen; nothing links here).
      { path: 'today', element: <Navigate to="/" replace /> },
      { path: 'chore/:occurrenceId', element: <ChoreDetail /> },
      {
        // The kid Missions shelf (ADR-0141) — the full gallery of anytime one-time chores, reached
        // from the Home Missions digest's "See all". LAZY (secondary surface, off the day critical path).
        path: 'missions',
        lazy: async () => {
          const { MissionsPage } = await import('@/pages/missions/MissionsPage');
          return { Component: withFeature(Feature.Chores, MissionsPage) };
        },
      },
      {
        // The kid Missions HISTORY shelf (ADR-0141) — the read-only scrapbook of finished missions,
        // reached from the Missions page's "See what you've finished". LAZY (secondary surface).
        path: 'missions/finished',
        lazy: async () => {
          const { MissionsHistoryPage } = await import('@/pages/missions/MissionsHistoryPage');
          return { Component: withFeature(Feature.Chores, MissionsHistoryPage) };
        },
      },
      {
        // The kid Habits focus view (today's habits + the kind streak surface). LAZY — kept out of
        // the kid critical-path bundle (the day screen already inlines a Habits section).
        path: 'habits',
        lazy: async () => {
          const { HabitsPage } = await import('@/pages/habits/HabitsPage');
          return { Component: withFeature(Feature.Habits, HabitsPage) };
        },
      },
      {
        // The kid's own habit detail — streak record, completions heatmap, read-only recent days.
        // Self-scoped server-side (a kid reads only their OWN habit). LAZY, off the critical path.
        path: 'habits/:id',
        lazy: async () => {
          const { MyHabitDetailPage } = await import('@/pages/habits/MyHabitDetailPage');
          return { Component: withFeature(Feature.Habits, MyHabitDetailPage) };
        },
      },
      {
        // Kid Learning — lands on the Learning MAP (ADR-0034: subjects → topic ladders). LAZY: the map +
        // session host + shells ship as their own chunks, kept out of the kid critical-path bundle.
        path: 'learn',
        lazy: async () => {
          const { LearnPage } = await import('@/pages/learn/LearnPage');
          return { Component: withFeature(Feature.Learning, LearnPage) };
        },
      },
      {
        // A bounded learning SESSION toward one topic — Start → Play → Results (ADR-0034). LAZY.
        path: 'learn/session/:topicId',
        lazy: async () => {
          const { LearnSessionPage } = await import('@/pages/learn/LearnSessionPage');
          return { Component: withFeature(Feature.Learning, LearnSessionPage) };
        },
      },
      {
        // Kid learning LIBRARY — the Books + Rules shelves behind one segmented page (plan-12). LAZY.
        path: 'learn/library',
        lazy: async () => {
          const { LibraryPage } = await import('@/pages/learn/LibraryPage');
          return { Component: withFeature(Feature.Learning, LibraryPage) };
        },
      },
      {
        // The full-screen BOOK PLAYER for one topic's teaching deck (ADR-0132 §3). LAZY.
        path: 'learn/library/books/:topicId',
        lazy: async () => {
          const { BookPlayerPage } = await import('@/pages/learn/BookPlayerPage');
          return { Component: withFeature(Feature.Learning, BookPlayerPage) };
        },
      },
      {
        // The kid's own learning progress (read-only map: topics + crowns + states). LAZY.
        path: 'learn/progress',
        lazy: async () => {
          const { LearningProgressPage } = await import('@/pages/learn/LearningProgressPage');
          return {
            Component: withFeature(Feature.Learning, LearningProgressPage),
          };
        },
      },
      {
        // Guardian "Our kids" overview — the landing for the bottom-nav "Family" tab (shown to
        // guardians who can't approve, e.g. Relatives). LAZY — kept out of the kid critical-path bundle.
        path: 'family',
        lazy: async () => {
          const { FamilyOverviewPage } = await import('@/pages/family-overview/FamilyOverviewPage');
          return { Component: FamilyOverviewPage };
        },
      },
      {
        // Self-service account/security (change password). Reachable from BOTH shells; rendered inside
        // the family shell so it inherits the chrome. LAZY — kept out of the kid critical-path bundle.
        path: 'account',
        lazy: async () => {
          const { AccountPage } = await import('@/pages/account/AccountPage');
          return { Component: AccountPage };
        },
      },
      {
        path: 'calendar',
        lazy: async () => {
          const { CalendarPage } = await import('@/pages/calendar/CalendarPage');
          return { Component: withFeature(Feature.Chores, CalendarPage) };
        },
      },
      {
        // Inbox — the single "needs my decision" surface (chore approvals + pending reward
        // redemptions, unified). LAZY — kept out of the kid critical-path bundle.
        path: 'inbox',
        lazy: async () => {
          const { InboxPage } = await import('@/pages/inbox/InboxPage');
          return { Component: InboxPage };
        },
      },
      // Legacy `/approvals` link/bookmark → the unified Inbox.
      { path: 'approvals', element: <Navigate to="/inbox" replace /> },
      {
        path: 'rewards',
        lazy: async () => {
          const { RewardsPage } = await import('@/pages/rewards/RewardsPage');
          return { Component: withFeature(Feature.Economy, RewardsPage) };
        },
      },
      {
        // Guardian-shell reward authoring (ADR-0091) — a parent/relative adds or edits a reward without
        // entering the command center. Same component serves new + edit; it self-gates on guardian +
        // `reward:create:own` (a kid deep-linking here is bounced back to the shop). LAZY.
        path: 'rewards/new',
        lazy: async () => {
          const { GuardianRewardEditPage } = await import('@/pages/rewards/GuardianRewardEditPage');
          return { Component: withFeature(Feature.Economy, GuardianRewardEditPage) };
        },
      },
      {
        path: 'rewards/:rewardId/edit',
        lazy: async () => {
          const { GuardianRewardEditPage } = await import('@/pages/rewards/GuardianRewardEditPage');
          return { Component: withFeature(Feature.Economy, GuardianRewardEditPage) };
        },
      },
      {
        // Kid "Ways to earn coins" (ADR-0080) — the read-only mirror of the parent Ways-to-earn surface:
        // what earns coins (goal · streak/mastery bonuses · mastery reward · chores) with live progress.
        // Reached from the Rewards header (the spend↔earn pairing), not a 6th bottom-nav tab. LAZY, off
        // the kid critical-path bundle; self-scoped server-side (a kid reads their own).
        path: 'ways-to-earn',
        lazy: async () => {
          // Persona picker: kid → cozy WaysToEarnPage; guardian (?child=) → warm-neutral GuardianWaysToEarnPage.
          const { WaysToEarnRoute } = await import('@/pages/ways-to-earn/WaysToEarnRoute');
          return { Component: withFeature(Feature.Economy, WaysToEarnRoute) };
        },
      },
      {
        // `/shop` — the kid COLLECT surface (ADR-0111): spend ✨ on new Heroes · Pets · Backgrounds. Reached
        // from the Hero page (the 5-tab kid nav has no Shop tab). Per-hero LOOKS are bought in `/wardrobe`.
        path: 'shop',
        lazy: async () => {
          const { CollectPage } = await import('@/pages/collect/CollectPage');
          return { Component: withFeature(Feature.Play, CollectPage) };
        },
      },
      {
        // `/wardrobe` — the kid dress-up surface (coins STYLE the hero; effort GROWS her). The Hero
        // page's "Одягнути" button links here. Renders the cosmetics shop in the cozy kid look; `/shop`
        // stays as a legacy alias to the same surface.
        path: 'wardrobe',
        lazy: async () => {
          const { ShopPage } = await import('@/pages/shop/ShopPage');
          return { Component: withFeature(Feature.Cosmetics, ShopPage) };
        },
      },
      {
        path: 'wallet',
        lazy: async () => {
          const { WalletPage } = await import('@/pages/wallet/WalletPage');
          return { Component: withFeature(Feature.Economy, WalletPage) };
        },
      },
      {
        path: 'savings',
        lazy: async () => {
          const { SavingsPage } = await import('@/pages/savings/SavingsPage');
          return { Component: withFeature(Feature.Economy, SavingsPage) };
        },
      },
      {
        // The notification center (ADR-0135) — the member's persona-themed "while you were away" history,
        // reached from the header bell. Behind Feature.Notifications (the deep-link backstop; the bell
        // hides too). Shared by kid + family personas. LAZY — off the critical path.
        path: 'notifications',
        lazy: async () => {
          const { NotificationsPage } = await import('@/pages/notifications/NotificationsPage');
          return { Component: withFeature(Feature.Notifications, NotificationsPage) };
        },
      },
      {
        // The kid's Hero destination (ADR-0095) — the chosen, named, evolving hero + a collapsible "Me"
        // section folding in the former profile. Behind Feature.Play (the deep-link backstop; the nav
        // hides the tab too). LAZY — kept out of the kid critical-path bundle.
        path: 'hero',
        lazy: async () => {
          const { HeroPage } = await import('@/pages/hero/HeroPage');
          return { Component: withFeature(Feature.Play, HeroPage) };
        },
      },
      {
        // `/profile` — the kid's own "Я" page (avatar, stats, PIN, wallet/savings links). The hero
        // overhaul UN-BURIES it (it is no longer folded into a collapsed "Me" accordion on /hero); the
        // Hero page links here via a clear "Я" row. The guardian `/family/:childId` profile below is
        // UNCHANGED (it still renders ProfilePage read-only for a guardian viewing a child).
        path: 'profile',
        lazy: async () => {
          const { ProfilePage } = await import('@/pages/profile/ProfilePage');
          return { Component: ProfilePage };
        },
      },
      {
        // Filterable, paginated ledger History — the dedicated home for the growing activity list the
        // day-to-day wallet/profile "recent" cards link into. A kid sees their own; a guardian picks a
        // child. LAZY — kept out of the kid critical-path bundle.
        path: 'history',
        lazy: async () => {
          const { HistoryPage } = await import('@/pages/history/HistoryPage');
          return { Component: withFeature(Feature.Economy, HistoryPage) };
        },
      },
      {
        // Guardian per-child cluster — one shared sub-nav shell (`ChildShell`) wrapping a child's
        // Overview · Chores · Wallet · Savings · Learning · Habits · Hero (+ History, reachable but not a tab), each
        // leaf reading `:childId` (mockup docs/parent-preview/12–16). The same leaf pages also back the
        // kid self-routes above, which are NOT wrapped — so the kid look is untouched.
        path: 'family/:childId',
        lazy: async () => {
          const { ChildShell } = await import('@/widgets/child-shell');
          return { Component: ChildShell };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { ProfilePage } = await import('@/pages/profile/ProfilePage');
              return { Component: ProfilePage };
            },
          },
          {
            // Guardian view of one child's chores — the detailed, navigable week (the per-child
            // counterpart to the household `/calendar`). Overview owns the KPIs + completion trend; this
            // is the day-by-day occurrence list, each chore linking to its detail for approval.
            path: 'chores',
            lazy: async () => {
              const { FamilyChoresPage } = await import('@/pages/chores/FamilyChoresPage');
              return { Component: withFeature(Feature.Chores, FamilyChoresPage) };
            },
          },
          {
            // Guardian per-child wallet — a dedicated warm-neutral view (no `.kid` cozy scope), separate
            // from the kid's cozy `WalletPage` at `/wallet` which stays untouched.
            path: 'wallet',
            lazy: async () => {
              const { GuardianWalletPage } = await import('@/pages/wallet/GuardianWalletPage');
              return { Component: withFeature(Feature.Economy, GuardianWalletPage) };
            },
          },
          {
            path: 'savings',
            lazy: async () => {
              const { SavingsPage } = await import('@/pages/savings/SavingsPage');
              return { Component: withFeature(Feature.Economy, SavingsPage) };
            },
          },
          {
            path: 'learning',
            lazy: async () => {
              const { LearningProgressPage } = await import('@/pages/learn/LearningProgressPage');
              return {
                Component: withFeature(Feature.Learning, LearningProgressPage),
              };
            },
          },
          {
            // NEW: guardian view of a child's habits — no family habits route existed before (only the
            // kid's own `/habits` or the admin console). Streak summary + month-consistency strips.
            path: 'habits',
            lazy: async () => {
              const { FamilyHabitsPage } = await import('@/pages/habits/FamilyHabitsPage');
              return { Component: withFeature(Feature.Habits, FamilyHabitsPage) };
            },
          },
          {
            // Guardian view of a child's CURRENT hero — the same staged figure the kid sees (shared
            // HeroStage, ADR-0102), read-only. Dressing/renaming stays the kid's own play.
            path: 'hero',
            lazy: async () => {
              const { FamilyHeroPage } = await import('@/pages/hero/FamilyHeroPage');
              return { Component: withFeature(Feature.Play, FamilyHeroPage) };
            },
          },
          {
            // Child-scoped History for the guardian view (same page; reads `:childId`).
            path: 'history',
            lazy: async () => {
              const { HistoryPage } = await import('@/pages/history/HistoryPage');
              return { Component: withFeature(Feature.Economy, HistoryPage) };
            },
          },
        ],
      },
    ],
  },
  {
    // Admin panel (Command Center) — a STANDALONE top-level shell, NOT nested under the family
    // PersonaShell, so the admin chrome never stacks on top of the family header/nav. Guarded + lazy
    // so the whole surface ships as its own chunk, kept out of the kid bundle. Children: Overview ·
    // Approvals · Members (+ Roles) · Chores · Economy · Rewards · Settings.
    path: '/admin',
    loader: protectedLoader,
    errorElement: <RouteError />,
    lazy: async () => {
      const { AdminGuard } = await import('@/pages/admin/AdminGuard');
      return { Component: AdminGuard };
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { OverviewPage } = await import('@/pages/admin/overview/OverviewPage');
          return { Component: OverviewPage };
        },
      },
      {
        // Inbox — the console's single decisions queue (chore approvals + pending reward
        // redemptions, unified; the old Rewards "pending" banner folded in here).
        path: 'inbox',
        lazy: async () => {
          const { InboxPage } = await import('@/pages/admin/inbox/InboxPage');
          return { Component: InboxPage };
        },
      },
      // Legacy `/admin/approvals` link/bookmark → the unified Inbox.
      { path: 'approvals', element: <Navigate to="/admin/inbox" replace /> },
      {
        path: 'members',
        lazy: async () => {
          const { MembersPage } = await import('@/pages/admin/members/MembersPage');
          return { Component: MembersPage };
        },
      },
      {
        // Custom-role authoring — a sub-route of Members (reachable from its header). Role ASSIGNMENT
        // moved onto the member editor; this page exists only to author custom roles, the opt-in
        // `custom_roles` capability (ADR-0078). Without it the page is hidden (the header link drops)
        // and a deep link lands on the disabled state.
        path: 'members/roles',
        lazy: async () => {
          const { RolesPage } = await import('@/pages/admin/roles/RolesPage');
          return { Component: withFeature(Feature.CustomRoles, RolesPage) };
        },
      },
      {
        // Full-route role editor (ADR-0033) — create a custom role; the capability grid renders inline.
        // Editing role definitions is the opt-in `custom_roles` capability (ADR-0078); without it the
        // editor is hidden (the list shows names only) and deep links land on the disabled state.
        path: 'members/roles/new',
        lazy: async () => {
          const { RoleEditPage } = await import('@/pages/admin/roles/RoleEditPage');
          return { Component: withFeature(Feature.CustomRoles, RoleEditPage) };
        },
      },
      {
        path: 'members/roles/:id/edit',
        lazy: async () => {
          const { RoleEditPage } = await import('@/pages/admin/roles/RoleEditPage');
          return { Component: withFeature(Feature.CustomRoles, RoleEditPage) };
        },
      },
      {
        // Full-route add-child editor (ADR-0033) — a child profile is a first-class object; the avatar
        // picker is an inline section.
        path: 'members/new',
        lazy: async () => {
          const { MemberEditPage } = await import('@/pages/admin/members/MemberEditPage');
          return { Component: MemberEditPage };
        },
      },
      {
        // Full-route edit-profile editor (ADR-0033).
        path: 'members/:id/edit',
        lazy: async () => {
          const { MemberEditPage } = await import('@/pages/admin/members/MemberEditPage');
          return { Component: MemberEditPage };
        },
      },
      {
        path: 'chores',
        lazy: async () => {
          const { ChoresPage } = await import('@/pages/admin/chores/ChoresPage');
          return { Component: withFeature(Feature.Chores, ChoresPage) };
        },
      },
      {
        // Full-route chore editor (ADR-0033) — create + edit live on a real URL, not a modal.
        path: 'chores/new',
        lazy: async () => {
          const { ChoreEditPage } = await import('@/pages/admin/chores/ChoreEditPage');
          return { Component: withFeature(Feature.Chores, ChoreEditPage) };
        },
      },
      {
        path: 'chores/:id/edit',
        lazy: async () => {
          const { ChoreEditPage } = await import('@/pages/admin/chores/ChoreEditPage');
          return { Component: withFeature(Feature.Chores, ChoreEditPage) };
        },
      },
      {
        path: 'habits',
        lazy: async () => {
          const { HabitsPage } = await import('@/pages/admin/habits/HabitsPage');
          return { Component: withFeature(Feature.Habits, HabitsPage) };
        },
      },
      {
        // Full-route habit editor (ADR-0033).
        path: 'habits/new',
        lazy: async () => {
          const { HabitEditPage } = await import('@/pages/admin/habits/HabitEditPage');
          return { Component: withFeature(Feature.Habits, HabitEditPage) };
        },
      },
      {
        path: 'habits/:id/edit',
        lazy: async () => {
          const { HabitEditPage } = await import('@/pages/admin/habits/HabitEditPage');
          return { Component: withFeature(Feature.Habits, HabitEditPage) };
        },
      },
      {
        // Guardian habit detail (ADR-0040) — streak + recent days with the kind backfill / excuse
        // affordances. LAZY, under the guarded admin group.
        path: 'habits/:id',
        lazy: async () => {
          const { HabitDetailPage } = await import('@/pages/habit-detail/HabitDetailPage');
          return { Component: withFeature(Feature.Habits, HabitDetailPage) };
        },
      },
      {
        // Cooperative family quests (ADR-0029) — guardian management of the shared household goal.
        path: 'quests',
        lazy: async () => {
          const { QuestsPage } = await import('@/pages/admin/quests/QuestsPage');
          return { Component: withFeature(Feature.Quests, QuestsPage) };
        },
      },
      {
        path: 'quests/new',
        lazy: async () => {
          const { QuestEditPage } = await import('@/pages/admin/quests/QuestEditPage');
          return { Component: withFeature(Feature.Quests, QuestEditPage) };
        },
      },
      {
        // Edit metadata of an existing quest (title, description, questType, endDate, rewardNote).
        // Objectives, completion rule, and startDate are immutable after creation.
        path: 'quests/:questId/edit',
        lazy: async () => {
          const { QuestEditPage } = await import('@/pages/admin/quests/QuestEditPage');
          return { Component: withFeature(Feature.Quests, QuestEditPage) };
        },
      },
      {
        path: 'economy',
        lazy: async () => {
          const { EconomyPage } = await import('@/pages/admin/economy/EconomyPage');
          return { Component: withFeature(Feature.Economy, EconomyPage) };
        },
      },
      {
        // Household-wide, filterable + paginated transaction History (the Economy "recent" panel links
        // here). Gated by ledger-read; the API enforces regardless.
        path: 'history',
        lazy: async () => {
          const { HistoryPage } = await import('@/pages/admin/history/HistoryPage');
          return { Component: withFeature(Feature.Economy, HistoryPage) };
        },
      },
      {
        path: 'rewards',
        lazy: async () => {
          const { RewardsPage } = await import('@/pages/admin/rewards/RewardsPage');
          return { Component: withFeature(Feature.Economy, RewardsPage) };
        },
      },
      {
        // Household reward categories (ADR-0036) — manage the reward taxonomy (icon/color/order,
        // archive-not-delete). Same `reward:manage` gate as the catalog; the page self-gates.
        path: 'rewards/categories',
        lazy: async () => {
          const { CategoriesPage } = await import('@/pages/admin/rewards/CategoriesPage');
          return { Component: withFeature(Feature.Economy, CategoriesPage) };
        },
      },
      {
        // Full-route reward editor (ADR-0033) — create + edit, with the category/emoji pickers inline.
        path: 'rewards/new',
        lazy: async () => {
          const { RewardEditPage } = await import('@/pages/admin/rewards/RewardEditPage');
          return { Component: withFeature(Feature.Economy, RewardEditPage) };
        },
      },
      {
        path: 'rewards/:id/edit',
        lazy: async () => {
          const { RewardEditPage } = await import('@/pages/admin/rewards/RewardEditPage');
          return { Component: withFeature(Feature.Economy, RewardEditPage) };
        },
      },
      {
        // Learning content authoring (ADR-0034 §7) — the household's custom content packs (topics).
        // LAZY; the page self-gates on `learning:author` (the nav + AdminGuard gate entry too).
        path: 'learning',
        lazy: async () => {
          const { LearningPage } = await import('@/pages/admin/learning/LearningPage');
          return { Component: withFeature(Feature.Learning, LearningPage) };
        },
      },
      {
        // Full-route content-pack editor (ADR-0033) — create a new pack (topic).
        path: 'learning/packs/new',
        lazy: async () => {
          const { PackEditPage } = await import('@/pages/admin/learning/PackEditPage');
          return { Component: withFeature(Feature.Learning, PackEditPage) };
        },
      },
      {
        path: 'learning/packs/:id/edit',
        lazy: async () => {
          const { PackEditPage } = await import('@/pages/admin/learning/PackEditPage');
          return { Component: withFeature(Feature.Learning, PackEditPage) };
        },
      },
      {
        // Per-child curriculum setup is now ONE surface — the curriculum browser's "set up for {child}" mode
        // (progress + skip + steer, ADR-0075). This legacy per-child route redirects into it (child
        // preselected) so old links + the command-center nav keep working without a second per-child page.
        path: 'learning/child/:childId',
        loader: ({ params }) =>
          redirect(`/admin/learning/curriculum?child=${encodeURIComponent(params.childId ?? '')}`),
      },
      {
        // Parent CURRICULUM BROWSER — the whole content surface (built-ins + packs) by subject, with the
        // read-only preview + manual start-point placement. Same `learning:author` gate as /admin/learning
        // (the page self-gates; the API re-enforces). LAZY.
        path: 'learning/curriculum',
        lazy: async () => {
          const { CurriculumBrowserPage } =
            await import('@/pages/admin/learning/CurriculumBrowserPage');
          return {
            Component: withFeature(Feature.Learning, CurriculumBrowserPage),
          };
        },
      },
      {
        // Read-only PREVIEW of one topic's sample items + their held answers (sub-route of the browser).
        path: 'learning/curriculum/topics/:topicId/preview',
        lazy: async () => {
          const { TopicPreviewPage } = await import('@/pages/admin/learning/TopicPreviewPage');
          return { Component: withFeature(Feature.Learning, TopicPreviewPage) };
        },
      },
      {
        path: 'settings',
        lazy: async () => {
          const { SettingsPage } = await import('@/pages/admin/settings/SettingsPage');
          return { Component: SettingsPage };
        },
      },
    ],
  },
  {
    // Standalone super-admin **console** (ADR-0072/0073/0077) — a top-level command-center shell OUTSIDE
    // the family/household shells, for `kind:'platform'` operators (and env-allowlisted owners). The
    // `/admin/platform` sub-page was folded into this single console. Guarded + lazy so the whole
    // surface ships as its own chunk, never in any household member's bundle. Children: Overview ·
    // Households (+ detail) · Admins · Logs (+ Roles, ADR-0078).
    path: '/platform',
    loader: platformLoader,
    errorElement: <RouteError />,
    lazy: async () => {
      const { PlatformShell } = await import('@/pages/platform/PlatformShell');
      return { Component: PlatformShell };
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { OverviewPage } = await import('@/pages/platform/OverviewPage');
          return { Component: OverviewPage };
        },
      },
      {
        path: 'households',
        lazy: async () => {
          const { HouseholdsPage } = await import('@/pages/platform/HouseholdsPage');
          return { Component: HouseholdsPage };
        },
      },
      {
        path: 'households/:householdId',
        lazy: async () => {
          const { HouseholdDetailPage } = await import('@/pages/platform/HouseholdDetailPage');
          return { Component: HouseholdDetailPage };
        },
      },
      {
        path: 'roles',
        lazy: async () => {
          const { PlatformRolesPage } = await import('@/pages/platform/PlatformRolesPage');
          return { Component: PlatformRolesPage };
        },
      },
      {
        // Platform notifications console (ADR-0135 §9) — delivery-health stats, recent dispatches
        // (metadata only), and the announcements composer. Platform-owner only (API-gated).
        path: 'notifications',
        lazy: async () => {
          const { NotificationsPage } = await import('@/pages/platform/NotificationsPage');
          return { Component: NotificationsPage };
        },
      },
      {
        path: 'templates',
        lazy: async () => {
          const { TemplatesPage } = await import('@/pages/platform/TemplatesPage');
          return { Component: TemplatesPage };
        },
      },
      {
        path: 'templates/new',
        lazy: async () => {
          const { TemplateEditorPage } = await import('@/pages/platform/TemplateEditorPage');
          return { Component: TemplateEditorPage };
        },
      },
      {
        path: 'templates/:templateId/edit',
        lazy: async () => {
          const { TemplateEditorPage } = await import('@/pages/platform/TemplateEditorPage');
          return { Component: TemplateEditorPage };
        },
      },
      {
        path: 'heroes',
        lazy: async () => {
          const { ContentCatalogPage } = await import('@/pages/platform/ContentCatalogPage');
          return { Component: ContentCatalogPage };
        },
      },
      {
        path: 'heroes/:kind/:id',
        lazy: async () => {
          const { ContentDetailPage } = await import('@/pages/platform/ContentDetailPage');
          return { Component: ContentDetailPage };
        },
      },
      {
        path: 'learning',
        lazy: async () => {
          const { PlatformCurriculumPage } =
            await import('@/pages/platform/PlatformCurriculumPage');
          return { Component: PlatformCurriculumPage };
        },
      },
      {
        path: 'admins',
        lazy: async () => {
          const { AdminsPage } = await import('@/pages/platform/AdminsPage');
          return { Component: AdminsPage };
        },
      },
      {
        path: 'system',
        lazy: async () => {
          const { LogsPage } = await import('@/pages/platform/LogsPage');
          return { Component: LogsPage };
        },
      },
      // Back-compat: the old Logs route now redirects to the System surface (ADR-0077).
      { path: 'logs', element: <Navigate to="/platform/system" replace /> },
    ],
  },
]);
